import {
  DayOfWeek,
  DaySchedule,
  DayGroup,
  TimeFrame,
  DAYS_ORDER,
  DAY_LABELS_SHORT
} from './types';

/**
 * Format time from 24hr to 12hr format
 */
export function formatTime12Hr(time: string): string {
  if (!time || time === '--:--') return '--:--';

  // Handle HH:MM:SS format
  const timeParts = time.split(':');
  const hours = parseInt(timeParts[0]);
  const minutes = parseInt(timeParts[1]);

  if (isNaN(hours) || isNaN(minutes)) return '--:--';

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Format time in 24hr format
 */
export function formatTime24Hr(time: string): string {
  if (!time || time === '--:--') return '--:--';

  // Handle HH:MM:SS format
  const timeParts = time.split(':');
  const hours = parseInt(timeParts[0]);
  const minutes = parseInt(timeParts[1]);

  if (isNaN(hours) || isNaN(minutes)) return '--:--';

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Format time from minutes since midnight to HH:MM
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Convert HH:MM to minutes since midnight
 */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Group consecutive days together
 */
export function groupDays(days: DayOfWeek[]): DayGroup[] {
  if (!days || days.length === 0) return [];

  const sortedDays = [...days].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));
  const groups: DayGroup[] = [];
  let currentGroup: DayOfWeek[] = [sortedDays[0]];

  for (let i = 1; i < sortedDays.length; i++) {
    const prevIndex = DAYS_ORDER.indexOf(sortedDays[i - 1]);
    const currIndex = DAYS_ORDER.indexOf(sortedDays[i]);

    if (currIndex === prevIndex + 1) {
      currentGroup.push(sortedDays[i]);
    } else {
      groups.push(createDayGroup(currentGroup));
      currentGroup = [sortedDays[i]];
    }
  }
  groups.push(createDayGroup(currentGroup));

  return groups;
}

/**
 * Create a day group with appropriate label
 */
function createDayGroup(days: DayOfWeek[]): DayGroup {
  let label: string;

  if (days.length === 7) {
    label = 'Every day';
  } else if (days.length === 5 && isWeekdays(days)) {
    label = 'Weekdays';
  } else if (days.length === 2 && isWeekend(days)) {
    label = 'Weekends';
  } else if (days.length === 1) {
    label = DAY_LABELS_SHORT[days[0]];
  } else {
    label = `${DAY_LABELS_SHORT[days[0]]}-${DAY_LABELS_SHORT[days[days.length - 1]]}`;
  }

  return {
    days,
    label,
    schedule: {
      enabled: true,
      timeSlot: { open: '09:00', close: '17:00' }
    }
  };
}

/**
 * Check if days are weekdays (Mon-Fri)
 */
function isWeekdays(days: DayOfWeek[]): boolean {
  const weekdays: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
  return days.length === 5 && days.every(day => weekdays.includes(day));
}

/**
 * Check if days are weekend (Sat-Sun)
 */
function isWeekend(days: DayOfWeek[]): boolean {
  return days.length === 2 && days.includes('SATURDAY') && days.includes('SUNDAY');
}

/**
 * Detect common patterns in store hours
 */
export function detectHourPatterns(stores: any[]): Map<string, string[]> {
  const patterns = new Map<string, string[]>();

  stores.forEach(store => {
    const pattern = generateHourPattern(store);
    if (!patterns.has(pattern)) {
      patterns.set(pattern, []);
    }
    patterns.get(pattern)!.push(store.storeNumber);
  });

  return patterns;
}

/**
 * Generate a pattern string from store hours
 */
function generateHourPattern(store: any): string {
  // Simplified pattern generation - can be made more sophisticated
  const occasions = store.occasions || store.businessHours || [];
  return occasions
    .map((o: any) => `${o.occasion}:${o.isEnabled}`)
    .sort()
    .join('|');
}

/**
 * Validate time range
 */
export function validateTimeRange(open: string, close: string): string | null {
  if (!open || !close) return 'Both open and close times are required';

  const openMin = timeToMinutes(open);
  const closeMin = timeToMinutes(close);

  if (openMin === closeMin) return 'Open and close times cannot be the same';

  // Allow overnight hours (close time next day)
  if (closeMin < openMin && closeMin > 360) { // 360 = 6 AM as a reasonable cutoff
    return 'Invalid time range';
  }

  return null;
}

/**
 * Check if two time ranges overlap
 */
export function timeRangesOverlap(
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean {
  // Handle overnight ranges
  if (end1 < start1) end1 += 1440; // Add 24 hours
  if (end2 < start2) end2 += 1440;

  return (start1 < end2 && start2 < end1);
}

/**
 * Format day range for display
 */
export function formatDayRange(days: DayOfWeek[]): string {
  if (!days || days.length === 0) return 'No days set';
  if (days.length === 7) return 'Every day';

  const groups = groupDays(days);
  return groups.map(g => g.label).join(', ');
}

/**
 * Parse store hours from API response to our format
 */
export function parseStoreHours(apiData: any): TimeFrame[] {
  if (!apiData) return [];

  // Handle both array and single object format
  const dataArray = Array.isArray(apiData) ? apiData : [apiData];

  return dataArray.map((item) => {
    // The data structure from Byte Graph has timeFrame nested inside
    const timeFrame = item.timeFrame || item;
    const offsetOpen = item.offsetOpen || 0;
    const offsetClose = item.offsetClose || 0;

    // Parse intervalStartTime - it might already be in HH:MM format
    let intervalStartTime = '00:00';
    if (timeFrame.intervalStartTime) {
      // If it's already in HH:MM format, use it directly
      if (typeof timeFrame.intervalStartTime === 'string' && timeFrame.intervalStartTime.includes(':')) {
        intervalStartTime = timeFrame.intervalStartTime.substring(0, 5); // Take only HH:MM
      } else {
        // Convert from minutes if it's a number
        const minutes = Number(timeFrame.intervalStartTime);
        intervalStartTime = minutesToTime(minutes);
      }
    }

    // Parse duration
    let duration = 0;
    if (timeFrame.intervalEndTime) {
      const [endHours, endMinutes] = timeFrame.intervalEndTime.split(':').map(Number);
      const [startHours, startMinutes] = intervalStartTime.split(':').map(Number);
      const endTotalMinutes = endHours * 60 + (endMinutes || 0);
      const startTotalMinutes = startHours * 60 + (startMinutes || 0);
      duration = endTotalMinutes - startTotalMinutes;
      if (duration < 0) duration += 1440; // Handle overnight hours
    } else if (timeFrame.duration !== undefined) {
      // Duration can be a string "HH:MM:SS" or a number
      if (typeof timeFrame.duration === 'string' && timeFrame.duration.includes(':')) {
        // Parse "HH:MM:SS" as a duration
        const parts = timeFrame.duration.split(':').map(Number);
        duration = parts[0] * 60 + (parts[1] || 0); // Convert hours and minutes to total minutes
      } else {
        duration = Number(timeFrame.duration);
      }
    }

    return {
      startDate: timeFrame.startDate,
      endDate: timeFrame.endDate,
      intervalStartTime: intervalStartTime,
      duration: duration,
      days: parseDays(timeFrame.days || timeFrame.dayRange),
      blackout: timeFrame.isBlackoutTimeframe || timeFrame.blackout || false,
      weight: timeFrame.weight || 1,
      offsetOpen: offsetOpen,
      offsetClose: offsetClose
    };
  });
}

/**
 * Calculate duration from open and close times
 */
function calculateDuration(open: string, close: string): number {
  if (!open || !close) return 0;

  const openMin = timeToMinutes(open);
  let closeMin = timeToMinutes(close);

  // Handle overnight hours
  if (closeMin < openMin) {
    closeMin += 1440; // Add 24 hours
  }

  return closeMin - openMin;
}

/**
 * Parse days array from various formats
 */
function parseDays(days: any): DayOfWeek[] {
  if (!days) return [];

  // Map of day numbers (1-7) to day names
  const dayNumberMap: Record<number, DayOfWeek> = {
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY',
    7: 'SUNDAY'
  };

  if (Array.isArray(days)) {
    return days.map(d => {
      // If it's a number, convert to day name
      if (typeof d === 'number') {
        return dayNumberMap[d];
      }
      // If it's already a day name, use it
      if (DAYS_ORDER.includes(d as DayOfWeek)) {
        return d as DayOfWeek;
      }
      return null;
    }).filter(d => d !== null) as DayOfWeek[];
  }
  if (typeof days === 'string') {
    return days.split(',').map(d => d.trim().toUpperCase()) as DayOfWeek[];
  }
  return [];
}

/**
 * Convert our format back to API format
 */
export function toApiFormat(timeFrame: TimeFrame): any {
  const openMinutes = timeToMinutes(timeFrame.intervalStartTime) + timeFrame.offsetOpen;
  const closeMinutes = openMinutes + timeFrame.duration - timeFrame.offsetClose;

  return {
    startDate: timeFrame.startDate,
    endDate: timeFrame.endDate,
    intervalStartTime: minutesToTime(openMinutes - timeFrame.offsetOpen),
    duration: timeFrame.duration,
    days: timeFrame.days,
    blackout: timeFrame.blackout,
    weight: timeFrame.weight,
    orderingDelayOpen: timeFrame.offsetOpen,
    orderingStopClose: timeFrame.offsetClose,
    openTime: minutesToTime(openMinutes),
    closeTime: minutesToTime(closeMinutes)
  };
}